import React, { useState, useEffect } from 'react';
import Layout from '../components/shared/Layout';
import { vacationAPI, employeeAPI, holidayAPI } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { Palmtree, Plus, X, CheckCircle2, XCircle, Clock, Ban } from 'lucide-react';
import { useConfirm } from '../components/shared/ConfirmDialog';

const statusBadge = (s) => {
  const map = {
    pending:   { label: 'Pendiente',  cls: 'badge-pending',   Icon: Clock },
    approved:  { label: 'Aprobado',   cls: 'badge-approved',  Icon: CheckCircle2 },
    rejected:  { label: 'Rechazado',  cls: 'badge-rejected',  Icon: XCircle },
    cancelled: { label: 'Cancelado',  cls: 'badge-cancelled', Icon: Ban },
  };
  const { label, cls, Icon } = map[s] || map.cancelled;
  return (
    <span className={`badge ${cls}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <Icon size={11} strokeWidth={2} /> {label}
    </span>
  );
};

// Calcula días hábiles excluyendo fines de semana y feriados
const calcBusinessDays = (start, end, holidaySet = new Set()) => {
  if (!start || !end) return 0;
  let count = 0;
  const cur = new Date(start + 'T00:00:00');
  const endDate = new Date(end + 'T00:00:00');
  if (cur > endDate) return 0;
  while (cur <= endDate) {
    const day = cur.getDay();
    const iso = cur.toLocaleDateString('en-CA'); // YYYY-MM-DD en local
    if (day !== 0 && day !== 6 && !holidaySet.has(iso)) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
};

export default function VacationsPage() {
  const confirm = useConfirm();
  const [requests, setRequests] = useState([]);
  const [profile,  setProfile]  = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ start_date: '', end_date: '', notes: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    const [vRes, pRes] = await Promise.all([vacationAPI.getMine(), employeeAPI.getMyProfile()]);
    setRequests(vRes.data);
    setProfile(pRes.data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await vacationAPI.create(form);
      toast.success('Solicitud enviada correctamente');
      setShowModal(false);
      setForm({ start_date: '', end_date: '', notes: '' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al enviar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (id) => {
    const ok = await confirm({
      title: '¿Cancelar solicitud?',
      message: 'Los días serán devueltos a tu saldo de vacaciones.',
      variant: 'warning',
      confirmLabel: 'Sí, cancelar',
    });
    if (!ok) return;
    try {
      await vacationAPI.cancel(id);
      toast.success('Solicitud cancelada');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al cancelar');
    }
  };

  const available = profile ? profile.vacation_days_total - profile.vacation_days_used : 0;
  const maxAdvance   = profile?.vacation_days_auto || 0;
  const minAllowed   = -maxAdvance;
  const canRequest   = available > minAllowed;
  const [holidaysInRange, setHolidaysInRange] = useState([]);

  useEffect(() => {
    if (form.start_date && form.end_date && form.end_date >= form.start_date) {
      holidayAPI.checkRange({ start_date: form.start_date, end_date: form.end_date })
        .then(r => setHolidaysInRange(r.data.holidays || []))
        .catch(() => setHolidaysInRange([]));
    } else {
      setHolidaysInRange([]);
    }
  }, [form.start_date, form.end_date]);

  const holidaySet = new Set(holidaysInRange.map(h => h.date.split('T')[0]));
  const businessDays = calcBusinessDays(form.start_date, form.end_date, holidaySet);
  const today = new Date().toISOString().split('T')[0];

  return (
    <Layout title="Mis vacaciones">
      <div className="page-header">
        <div>
          <div className="page-title">Mis vacaciones</div>
          <div className="page-subtitle">
            {available} días disponibles de {profile?.vacation_days_total}
            {available < 0 ? ' (en adelanto)' : ''} — las solicitudes pendientes ya están descontadas
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)} disabled={!canRequest}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={16} /> Solicitar vacaciones
        </button>
      </div>

      {available < 0 && (
        <div className="alert alert-info">
          Tienes vacaciones adelantadas: debes {Math.abs(available)} día(s). Máximo de adelanto permitido: {maxAdvance} días según tu antigüedad.
        </div>
      )}
      {!canRequest && (
        <div className="alert alert-error">Alcanzaste el máximo de días que puedes adelantar {maxAdvance} días según tu antigüedad.</div>
      )}

      {profile && (
        <div className="card section-gap">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span className="text-sm font-medium">Días utilizados: {profile.vacation_days_used}</span>
            <span className="text-sm text-gray">Disponibles: <strong>{available}</strong></span>
          </div>
          <div className="days-bar-track" style={{ height: 10 }}>
            <div
              className={`days-bar-fill ${available < 3 ? 'danger' : available < 6 ? 'warn' : ''}`}
              style={{ width: `${(profile.vacation_days_used / Math.max(profile.vacation_days_total, 1)) * 100}%` }}
            />
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div className="card-title">Historial de solicitudes</div>
        </div>
        {requests.length === 0 ? (
          <div className="empty-state">
            <Palmtree size={40} color="var(--gray-300)" style={{ margin: '0 auto 12px', display: 'block' }} />
            <div className="empty-state-title">Aún no tienes solicitudes</div>
            <div>Haz clic en "Solicitar vacaciones" para comenzar</div>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr><th>Desde</th><th>Hasta</th><th>Días</th><th>Estado</th><th>Notas</th><th>Solicitud</th><th></th></tr>
              </thead>
              <tbody>
                {requests.map(r => (
                  <tr key={r.id}>
                    <td>
                      {r.start_date.slice(0, 10).split('-').reverse().join('/')}
                    </td>
                    <td>
                      {r.end_date.slice(0, 10).split('-').reverse().join('/')}
                    </td>
                    <td><strong>{r.total_days}</strong></td>
                    <td>{statusBadge(r.status)}</td>
                    <td style={{ maxWidth: 160, color: 'var(--gray-500)', fontSize: 13 }} className="truncate">{r.notes || '—'}</td>
                    <td style={{ color: 'var(--gray-500)', fontSize: 13 }}>{format(new Date(r.created_at), 'dd/MM/yyyy')}</td>
                    <td>
                      {r.status === 'pending' && (
                        <button className="btn btn-secondary btn-sm" onClick={() => handleCancel(r.id)}
                          style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <X size={13} /> Cancelar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Palmtree size={18} color="var(--primary)" /> Solicitar vacaciones
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="alert alert-info">
                  Días disponibles: <strong>{available}</strong>
                  {maxAdvance > 0 && (
                    <>
                      {' '}· Máximo adelanto: <strong>{maxAdvance}</strong>
                      {' '}· Puedes solicitar aún: <strong>{Math.max(0, maxAdvance + available)} días</strong>
                    </>
                  )}
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Fecha de inicio *</label>
                      <input type="date" className="form-input" value={form.start_date} min={today} required
                        onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Fecha de fin *</label>
                      <input type="date" className="form-input" value={form.end_date} min={form.start_date || today} required
                        onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
                  </div>
                </div>
                {/* Preview días hábiles */}
                {businessDays > 0 && (() => {
                  const wouldExceed = (available - businessDays) < minAllowed;
                  return (
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 14px', borderRadius: 8, marginBottom: holidaysInRange.length > 0 ? 8 : 12,
                      background: wouldExceed ? 'var(--danger-light)' : 'var(--primary-light)',
                      border: `1px solid ${wouldExceed ? '#fecaca' : '#bfdbfe'}`,
                    }}>
                      <span style={{ fontSize: 13, color: wouldExceed ? 'var(--danger)' : 'var(--primary)' }}>
                        Días hábiles solicitados:
                      </span>
                      <span style={{ fontWeight: 700, fontSize: 18, color: wouldExceed ? 'var(--danger)' : 'var(--primary)' }}>
                        {businessDays}d
                      </span>
                    </div>
                  );
                })()}

                {/* Aviso de feriados dentro del rango */}
                {holidaysInRange.length > 0 && (
                  <div style={{
                    padding: '10px 14px', borderRadius: 8, marginBottom: 12,
                    background: '#fffbeb', border: '1px solid #fcd34d',
                  }}>
                    <div style={{ fontWeight: 600, fontSize: 12, color: '#92400e', marginBottom: 4 }}>
                      ⚠️ {holidaysInRange.length} feriado(s) en el rango — no se cuentan como días hábiles
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#78350f' }}>
                      {holidaysInRange.map(h => (
                        <li key={h.date}>
                          {new Date(h.date.split('T')[0] + 'T00:00:00').toLocaleDateString('es', { day: '2-digit', month: 'long' })} — {h.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Notas (opcional)</label>
                  <textarea className="form-input form-textarea" placeholder="Motivo o comentarios..."
                    value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary"
                  disabled={loading || (businessDays > 0 && (available - businessDays) < minAllowed)}>
                  {loading ? 'Enviando…' : 'Enviar solicitud'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
